import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { find } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { UrlBuilder } from '../core/url-builder/url-builder';
import { InvestigatorSitesStorage } from '../sites/investigator-sites-storage';
import { SiteInfo } from './extended-site-info';
import { siteDisplayNameRenderer } from '../renderer/site-display-name-renderer';
import { unresolvedEQueryStatuses } from '../e-queries/models/e-query-status';
import { PendoService } from '../shared/pendo-service/pendo-service';

@Component({
    selector: 'site-card',
    template: require('./site-card.component.html'),
    styles: [require('./site-card.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteCardComponent implements OnDestroy {

    @Input()
    public siteInfo: SiteInfo;

    public isFavorite = new Subject<boolean>();

    public get sponsor() {
        return this.siteInfo.site.study.sponsor;
    }

    public get clientProtocol() {
        return this.siteInfo.site.study.clientProtocol;
    }

    public get siteDisplayName() {
        return siteDisplayNameRenderer({siteNumber: this.siteInfo.site.siteNum, investigatorFullName: this.siteInfo.site.investigatorFullName});
    }

    public get showLoadingProgress(): boolean {
        return !this.siteInfo.countersReady || !this.siteInfo.eQueryInfoReady;
    }

    private isFavoriteFlow = this.isFavorite
        .asObservable()
        .pipe(
            distinctUntilChanged(),
            switchMap(() => this.sitesStorage.saveSiteIsFavorite(this.siteInfo.site))
        );

    private subscription: Subscription;

    constructor(
        private sitesStorage: InvestigatorSitesStorage,
        changeDetector: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private router: Router,
        private pendoService: PendoService) {
        this.subscription = this.isFavoriteFlow.subscribe(
            () => {
                this.siteInfo.site.isFavorite = !this.siteInfo.site.isFavorite;
                changeDetector.markForCheck();
            },
            () => console.error('favorite status saving error'));
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public toggleFavorite() {
        this.isFavorite.next(!this.siteInfo.site.isFavorite);
    }

    public goToStudy() {
        this.pendoService.updateInvestigatorPendoDetails(this.siteInfo.site);
        const firstCategoryWithData = find(this.siteInfo.unreadInfo, x => x.totalDocs !== 0);

        if (firstCategoryWithData) {
            return this.router.navigate(firstCategoryWithData.url);
        }

        const eQueryInfo = this.siteInfo.eQueryInfo;

        if (eQueryInfo && eQueryInfo.total) {
            return this.router.navigate(eQueryInfo.url);
        }

        return this.urlBuilder
            .esa()
            .site(this.siteInfo.site)
            .subject()
            .navigate();
    }
}
